import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import debounce from 'lodash/debounce';
import numeral from 'numeral';
import { analytics, calculate } from 'libs';
import { LockIcon } from 'icons';
import Filter from './Filter';
import Text from './Text';

/**
 * Separate debounce trackers are required so each field change is tracked.
 * If the same debounce tracker is used for all fields, then earlier events
 * that belong to one field may be ignored when another field changes
 * within the debounce period.
 *
 * The debounce duration matches `analytics.productSearch.filterDebounced`.
 */
const trackLTVChange = debounce((value) => analytics.productSearch.filter({ ltv: value || 'cleared' }), 400);
const trackMortgageAmountChange = debounce(
  (value) => analytics.productSearch.filter({ mortgage_amount: value || 'cleared' }),
  400
);
const trackPropertyValueChange = debounce(
  (value) => analytics.productSearch.filter({ property_value: value || 'cleared' }),
  400
);

interface LTVCalculationFilterProps {
  filters: {
    ltv?: number | string;
    mortgage_amount?: number | string;
    property_value?: number | string;
  };
  onFilter: (update: { ltv?: number | null; mortgage_amount?: number | null; property_value?: number | null }) => void;
}

const LTVCalculationFilter = ({ filters, onFilter }: LTVCalculationFilterProps) => {
  const { ltv: initialLTV, mortgage_amount: initialMortgageAmount, property_value: initialPropertyValue } = filters;

  const [ltv, setLTV] = useState<any>(initialLTV);
  const [mortgageAmount, setMortgageAmount] = useState<any>(initialMortgageAmount);
  const [propertyValue, setPropertyValue] = useState<any>(initialPropertyValue);

  const onFilterRef = useRef(onFilter);
  onFilterRef.current = onFilter;

  const onDebouncedFilter = useMemo(
    () =>
      debounce((patch) => {
        onFilterRef.current(patch);
      }, 400),
    []
  );

  useEffect(() => {
    onDebouncedFilter.cancel();
    setLTV(filters.ltv);
    setMortgageAmount(filters.mortgage_amount);
    setPropertyValue(filters.property_value);
  }, [filters.ltv, filters.mortgage_amount, filters.property_value, onDebouncedFilter]);

  useEffect(() => () => onDebouncedFilter.cancel(), [onDebouncedFilter]);

  const isLTVAutoCalculated = (newMortgageAmount: any, newPropertyValue: any) =>
    Boolean(newMortgageAmount && newPropertyValue);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    let newLTV = ltv;
    let newMortgageAmount = mortgageAmount;
    let newPropertyValue = propertyValue;

    switch (name) {
      case 'ltv':
        newLTV = numeral(value).value();
        trackLTVChange(newLTV);
        break;
      case 'mortgage_amount':
        newMortgageAmount = numeral(value).value();
        trackMortgageAmountChange(newMortgageAmount);
        break;
      case 'property_value':
        newPropertyValue = numeral(value).value();
        trackPropertyValueChange(newPropertyValue);
        break;
      default:
        break;
    }

    if (isLTVAutoCalculated(newMortgageAmount, newPropertyValue)) {
      newLTV = parseInt(calculate.ltv(newMortgageAmount, newPropertyValue) as unknown as string, 10);
      trackLTVChange(newLTV);
    }

    setLTV(newLTV);
    setMortgageAmount(newMortgageAmount);
    setPropertyValue(newPropertyValue);

    onDebouncedFilter({ ltv: newLTV, mortgage_amount: newMortgageAmount, property_value: newPropertyValue });
  };

  const formatValues = () => ({
    formattedLTV: ltv ? numeral(ltv).format() : '',
    formattedMortgageAmount: mortgageAmount ? numeral(mortgageAmount).format('$0,0') : '',
    formattedPropertyValue: propertyValue ? numeral(propertyValue).format('$0,0') : '',
  });

  const { formattedLTV, formattedMortgageAmount, formattedPropertyValue } = formatValues();
  const isCalculated = isLTVAutoCalculated(mortgageAmount, propertyValue);

  return (
    <>
      <Filter name="Mortgage Amount">
        <Text
          testId="filter-mortgage-amount"
          name="mortgage_amount"
          onChange={handleChange}
          placeholder="Enter mortgage amount"
          value={formattedMortgageAmount}
        />
      </Filter>
      <Filter name="Property Value">
        <Text
          testId="filter-property-value"
          name="property_value"
          onChange={handleChange}
          placeholder="Enter property value"
          value={formattedPropertyValue}
        />
      </Filter>
      <Filter name={`LTV${isCalculated ? ' (calculated)' : ''}`}>
        <div className="position-relative">
          <Text
            testId="filter-ltv"
            disabled={isCalculated}
            name="ltv"
            onChange={handleChange}
            placeholder="Enter LTV"
            value={formattedLTV}
          />
          {isCalculated && (
            <div className="position-absolute inset-y-0 right-0 d-flex flex-column justify-content-center px-2">
              <LockIcon className="w-6 opacity-80" />
            </div>
          )}
        </div>
      </Filter>
    </>
  );
};

export default LTVCalculationFilter;
